import React, { useEffect, useState } from 'react'
import styles from './syles.module.scss'
import Link from 'next/link'
import packageJson from '../../../../../package.json';
import { useShoppingCart } from '@/src/context/shopping-cart/shopping-cart-context'
import { useMerchant } from '@/src/context/merchant/merchant-context'
import { useTranslations } from 'next-intl';
import { signOut } from 'next-auth/react';
import TrackOrderHeader from './track-order-header/track-order-header';
import SvgLogin from '@/src/assets/svgs/collection/login.svg'
import SvgLogout from '@/src/assets/svgs/collection/logout.svg'
import SvgBag from '@/src/assets/svgs/collection/bag.svg'
import SvgMeal from '@/src/assets/svgs/collection/meal.svg'
import SvgOrder from '@/src/assets/svgs/collection/order.svg'
import SvgUser from '@/src/assets/svgs/collection/user.svg'
import SvgInfo from '@/src/assets/svgs/collection/info.svg'
import SvgHome from '@/src/assets/svgs/collection/home-o.svg'
import SvgDiscount from '@/src/assets/svgs/collection/discount.svg'
import SvgStar from '@/src/assets/svgs/collection/star.svg'
import { useCustomer } from '@/src/context/customer/customer-context';
import { usePathname, useRouter } from 'next/navigation';
import MerchantLogo from '@/src/components/merchant/logo/merchant-logo';
import Modal from '@/src/components/helpers/overlays/modal/modal';
import MerchantProfile from '@/src/components/merchant/profile/merchant-profile';
import OrderGet from '@/src/interfaces/sales/order-get';
import { Overlay } from '@/src/components/helpers/overlays/overlay';

interface Props {
    orderStatus?: OrderGet.StatusResponse
}

interface DefaultItem {
    title: string
    svg: React.JSX.Element
    redirect: string
    canShow: boolean
    isLogout?: boolean
    onClick?(): void
}

interface NavItem extends DefaultItem {
    isAuthNeeded: boolean
}

export default function ContentDrawer(props: Props) {
    const { merchantSummary, merchantDetailed } = useMerchant()
    const { shoppingCart } = useShoppingCart()
    const { customer, customerLoginStatus } = useCustomer()
    const router = useRouter()
    const t = useTranslations('drawer')
    const pathname = usePathname()

    const env = process.env.NEXT_PUBLIC_APP_ENV ?? ''
    const multiStoreUrl = merchantDetailed?.model?.multiStoreUrl

    const [modal, setModal] = useState(false)

    const navItems: NavItem[] = [
        {
            title: t('login'),
            svg: <SvgLogin />,
            canShow: customerLoginStatus !== 'loggedIn',
            isAuthNeeded: false,
            redirect: "/entrar"
        },
        {
            title: t('meal'),
            svg: <SvgMeal />,
            canShow: true,
            isAuthNeeded: false,
            redirect: "/"
        },
        {
            title: t('bag'),
            svg: <SvgBag />,
            canShow: (shoppingCart?.items && shoppingCart?.items.length > 0) ?? false,
            isAuthNeeded: false,
            redirect: "/carrinho"
        },
        {
            title: t('menu'),
            svg: <SvgOrder />,
            canShow: customer?.authSource !== 'Convidado',
            isAuthNeeded: true,
            redirect: "/pedidos"
        },
        {
            title: t('user'),
            svg: <SvgUser />,
            canShow: customer?.authSource !== 'Convidado',
            isAuthNeeded: true,
            redirect: "/meu-cadastro"
        },
        {
            title: customer?.authSource === 'Convidado' ? 'Sair do modo convidado' : t('logout'),
            svg: <SvgLogout />,
            canShow: true,
            isAuthNeeded: true,
            redirect: pathname,
            isLogout: true,
            onClick: async () => {
                await signOut({ redirect: false });
                router.refresh()
            }
        },
    ]

    const userItems: NavItem[] = [
        {
            title: t('coupon'),
            svg: <SvgDiscount />,
            canShow: true,
            redirect: "/cupons",
            isAuthNeeded: false
        },
        {
            title: t('loyalty'),
            svg: <SvgStar />,
            canShow: customerLoginStatus === 'loggedIn' && customer?.authSource !== 'Convidado',
            redirect: "/fidelidade",
            isAuthNeeded: true
        },
    ]

    const bottomItems: NavItem[] = [
        {
            title: 'Lojas',
            svg: <SvgHome />,
            redirect: `/lojas/${multiStoreUrl}`,
            canShow: !!multiStoreUrl,
            isAuthNeeded: false
        },
    ]

    const handleItemOnClick = (item: NavItem) => {
        if (item.onClick)
            item.onClick()

        if (item.redirect === pathname && !item.isLogout)
            window.location.reload()
    }

    const itemElement = (item: NavItem, key: number) => {
        return (
            <Link onClick={() => handleItemOnClick(item)} href={item.redirect} key={key} className={styles.navigationItem}>
                <div className={styles.svgArea}>
                    {item.svg}
                </div>
                <span>{item.title}</span>
            </Link>
        )
    }

    const renderBottomInfo = () => {
        const envLower = env.toLowerCase()
        let info: string = ''

        if (merchantDetailed?.model?.consumerVersion)
            info += merchantDetailed?.model?.consumerVersion

        if (merchantDetailed?.model?.lastPublishedAt) {
            const date = new Date(merchantDetailed.model.lastPublishedAt)

            const formattedDate = date
                .toLocaleString('pt-BR', {
                    year: '2-digit',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                })
                .replace(/[^\d]/g, '')

            info += ` (${formattedDate})`
        }

        const _info = info ? info.trim() + ' - ' : <></>

        if (envLower === 'staging') {
            return <span className='font-sm' title={process.env.NEXT_PUBLIC_APP_ENV}>{_info}BETA</span>
        }

        if (envLower !== 'production') {
            return <span className='font-sm' title={process.env.NEXT_PUBLIC_APP_ENV}>{_info}SANDBOX</span>
        }

        return <span className='font-sm'>{info ? info.trim() : <></>}</span>
    }

    const openProfile = () => {
        Overlay.openWithQs('q', 'perfil', () => setModal(true))
    }

    useEffect(() => {
        Overlay.startPopstateListener(() => setModal(false))
    }, [])

    return (
        <>
            {
                modal &&
                <Modal
                    isInterceptRouting={false}
                    options={{
                        headProps: {
                            elToObserveId: "merchant-title", headTitle: merchantSummary?.name,
                            backgroundHeadTheme: "primary"
                        }
                    }}
                    onClose={() => setModal(false)}
                    isOpen={modal}
                >
                    <MerchantProfile />
                </Modal>
            }
            <aside className={styles.container}>
                <div className={styles.drawerHead}>
                    {
                        customer && customer.authSource === 'Convidado' ?
                            <span className={`${styles.customerName} font-sm`}> Olá, {customer.name}! <i className='font-xsm' style={{ opacity: 0.7 }}>{'(convidado)'}</i></span>
                            :
                            customer ?
                                <span className={`${styles.customerName} font-sm`}> Olá, {customer.name}!</span>
                                :
                                <div />
                    }
                </div>
                {
                    merchantSummary?.name &&
                    <Link href={'/'} className={styles.merchantArea}>
                        <MerchantLogo className={styles.logoArea} />
                        <h3 className={`${styles.title} font-md`}>{merchantSummary?.name}</h3>
                    </Link>
                }
                {
                    (customerLoginStatus === 'loggedIn' && props.orderStatus && !pathname.startsWith('/pedido/acompanhar')) &&
                    <TrackOrderHeader orderStatus={props.orderStatus} />
                }
                <div className={styles.navigationArea}>
                    {
                        navItems.map((item, index) => (
                            item.isAuthNeeded === false && item.canShow
                                ? itemElement(item, index)
                                : item.isAuthNeeded && customerLoginStatus === 'loggedIn' && item.canShow
                                    ? itemElement(item, index)
                                    : <React.Fragment key={index}></React.Fragment>
                        ))
                    }
                </div>
                <div className={styles.navigationArea}>
                    {
                        userItems.map((item, index) => (
                            item.canShow && itemElement(item, index)
                        ))
                    }
                </div>
                <div className={styles.navigationArea} style={{ borderBottom: '0' }}>
                    <div onClick={openProfile} className={styles.navigationItem}>
                        <div className={styles.svgArea}>
                            <SvgInfo />
                        </div>
                        <span>{t('about')}</span>
                    </div>
                    {
                        bottomItems.map((item, index) => (
                            item.canShow && itemElement(item, index)
                        ))
                    }
                </div>
                <div className={styles.developedByArea}>
                    <div className={styles.developed}>
                        <span className='font-sm'>{t('developed')}</span>
                        <div className={styles.logoMenuDinoArea}>
                            <img draggable={false} className={styles.logoMenuDino} src='https://menudinofiles.blob.core.windows.net/padroes/menudino-logo-menor.png' alt='MenuDino' />
                        </div>
                        <span className='font-sm'>v{packageJson.version} - {new Date().getFullYear()} (C)</span>
                        {renderBottomInfo()}
                    </div>
                </div>
            </aside>
        </>
    )
}
